import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { RootState } from '../../store';
import { fetchArtifacts } from '../../store/slices/ArtifactSlice';
import { useArtifactTree } from '../../contexts';
import { useUser } from '../../contexts/UserContext';
import { CompanyProcessService } from '../../services/companyProcessService';
import { ArtifactProcessService } from '../../services/api/artifactProcessService';
import { TransitionService } from '../../services/api/transitionService';
import { ArtifactItemService } from '../../services/api/artifactItemService';
import { StateMachineService } from '../../services/api/stateMachineService';
import { EnhancedArtifact } from '../../types/artifacts';
import { ArtifactItem } from '../../types/artifactItems';
import { AvailableTransition } from '../../types/stateMachine';
import { Table, Button, Modal, Form, Input, message, Tag, Space, Card, Tabs } from 'antd';
import { PlayCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import './CompanyProcessPage.css';
import { COMPANY_ARTIFACTS_SELECT_PATH } from '../../utils/companyArtifactRoutes';

interface CompanyProcessPageProps {
  // Optional props for direct usage
  artifactId?: string;
  processId?: string;
  entryType?: 'artifact' | 'process';
}

interface Process {
  id: string;
  name: string;
  description?: string;
  process_type: string;
  demand_flow_type: string;
  artifact_ref: string;
  created_at?: string;
  updated_at?: string;
}

interface ProcessTransition {
  id: string;
  name: string;
  description?: string;
  artifact_ref: string;
  artifact_name?: string;
  input_state_ref: string;
  input_state_name?: string;
  output_state_ref: string;
  output_state_name?: string;
  transition_type: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface ArtifactWithProcesses extends EnhancedArtifact {
  processes: Process[];
}

export const CompanyProcessPage: React.FC<CompanyProcessPageProps> = ({
  artifactId: _propArtifactId,
  processId: propProcessId,
  entryType: _propEntryType,
}) => {
  const { processId: paramProcessId } = useParams<{
    artifactId?: string;
    processId?: string;
  }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Get the selected tree from context
  const { selectedTree } = useArtifactTree();
  const { selectedCompany } = useUser();

  // Get artifacts from Redux store
  const {
    hierarchy,
    loading: artifactsLoading,
    error: artifactsError,
  } = useAppSelector((state: RootState) => state.artifacts);

  const [artifactsWithProcesses, setArtifactsWithProcesses] = useState<ArtifactWithProcesses[]>([]);
  const [processesLoading, setProcessesLoading] = useState(false);
  const [processesError, setProcessesError] = useState<string | null>(null);
  const [expandedArtifacts, setExpandedArtifacts] = useState<Set<string>>(new Set());
  const [selectedProcess, setSelectedProcess] = useState<Process | null>(null);
  const [processTransitions, setProcessTransitions] = useState<ProcessTransition[]>([]);
  const [transitionsLoading, setTransitionsLoading] = useState(false);
  const [transitionsError, setTransitionsError] = useState<string | null>(null);
  
  // Queue view state
  const [queueItems, setQueueItems] = useState<ArtifactItem[]>([]);
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueError, setQueueError] = useState<string | null>(null);
  const [selectedItemForTransition, setSelectedItemForTransition] = useState<ArtifactItem | null>(null);
  const [availableTransitions, setAvailableTransitions] = useState<AvailableTransition[]>([]);
  const [transitionsLoadingForItem, setTransitionsLoadingForItem] = useState(false);
  const [showTransitionModal, setShowTransitionModal] = useState(false);
  const [executingTransition, setExecutingTransition] = useState(false);
  const [transitionForm] = Form.useForm();
  const [activeView, setActiveView] = useState<'processes' | 'queue'>('processes');

  // Load a specific process by ID
  const loadProcessById = useCallback(async (processId: string) => {
    try {
      console.log(`[CompanyProcessPage] Loading specific process by ID: ${processId}`);
      const artifactProcess = await ArtifactProcessService.getById(processId);
      const process: Process = {
        id: artifactProcess.id,
        name: artifactProcess.name,
        description: artifactProcess.description,
        process_type: artifactProcess.process_type,
        demand_flow_type: artifactProcess.demand_flow_type,
        artifact_ref: artifactProcess.artifact_ref || '',
        created_at: artifactProcess.created_at,
        updated_at: artifactProcess.updated_at,
      };
      console.log(`[CompanyProcessPage] Loaded process:`, process);
      setSelectedProcess(process);
      return process;
    } catch (error) {
      console.error(`[CompanyProcessPage] Failed to load process ${processId}:`, error);
      setProcessesError(
        `Failed to load process: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
      return null;
    }
  }, []);

  // Load process transitions
  const loadProcessTransitions = useCallback(async (processId: string) => {
    try {
      console.log(`[CompanyProcessPage] Loading transitions for process: ${processId}`);
      setTransitionsLoading(true);
      setTransitionsError(null);

      const transitions = await TransitionService.getTransitionsByProcess(processId);
      console.log(`[CompanyProcessPage] Found ${transitions.length} transitions:`, transitions);

      // Map ArtifactItemTransition to ProcessTransition
      const processTransitions: ProcessTransition[] = transitions.map((transition) => ({
        id: transition.id,
        name: transition.name,
        description: transition.description,
        artifact_ref: transition.artifact_ref,
        artifact_name: transition.artifact_ref, // We'll need to get the actual name
        input_state_ref: transition.input_state_ref,
        input_state_name: transition.input_state_ref, // We'll need to get the actual name
        output_state_ref: transition.output_state_ref,
        output_state_name: transition.output_state_ref, // We'll need to get the actual name
        transition_type: transition.transition_type,
        is_active: transition.is_active,
        created_at: transition.created_at,
        updated_at: transition.updated_at,
      }));

      setProcessTransitions(processTransitions);
    } catch (error) {
      console.error(
        `[CompanyProcessPage] Failed to load transitions for process ${processId}:`,
        error
      );
      setTransitionsError(
        `Failed to load transitions: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setTransitionsLoading(false);
    }
  }, []);

  // Load processes for all artifacts
  const loadProcessesForArtifacts = useCallback(async () => {
    if (!hierarchy.length) {
      console.log('[CompanyProcessPage] No hierarchy to load processes for');
      return;
    }

    console.log(
      '[CompanyProcessPage] Loading processes for artifacts:',
      hierarchy.map((a) => ({ id: a.id, name: a.name }))
    );
    setProcessesLoading(true);
    setProcessesError(null);

    try {
      const artifactsWithProcessesData: ArtifactWithProcesses[] = [];

      for (const artifact of hierarchy) {
        try {
          console.log(
            `[CompanyProcessPage] Fetching company processes for artifact ${artifact.id} (${artifact.name})`
          );
          // Fetch company processes for this artifact
          const companyProcessService = new CompanyProcessService();
          const companyProcesses = await companyProcessService.getProcessesForArtifact(artifact.id);
          console.log(
            `[CompanyProcessPage] Found ${companyProcesses.length} company processes for artifact ${artifact.id}:`,
            companyProcesses
          );

          const processes: Process[] = companyProcesses.map((companyProcess) => ({
            id: companyProcess.id,
            name: companyProcess.name,
            description: companyProcess.description,
            process_type: companyProcess.process_type,
            demand_flow_type: companyProcess.demand_flow_type,
            artifact_ref: companyProcess.artifact_ref || '',
            created_at: companyProcess.created_at,
            updated_at: companyProcess.updated_at,
          }));
          artifactsWithProcessesData.push({
            ...artifact,
            processes: processes || [],
          });
        } catch (error) {
          console.warn(`Failed to load processes for artifact ${artifact.id}:`, error);
          artifactsWithProcessesData.push({
            ...artifact,
            processes: [],
          });
        }
      }

      console.log(
        '[CompanyProcessPage] Final artifacts with processes:',
        artifactsWithProcessesData
      );
      setArtifactsWithProcesses(artifactsWithProcessesData);
    } catch (error) {
      console.error('Error loading processes:', error);
      setProcessesError('Failed to load processes');
    } finally {
      setProcessesLoading(false);
    }
  }, [hierarchy]);

  // Load artifacts when tree changes
  useEffect(() => {
    if (selectedTree) {
      void dispatch(fetchArtifacts({ treeId: selectedTree.id, companyId: selectedCompany?.id }));
    }
  }, [dispatch, selectedTree, selectedCompany?.id]);

  // Load processes when artifacts change
  useEffect(() => {
    void loadProcessesForArtifacts();
  }, [loadProcessesForArtifacts]);

  // Load specific process if processId is provided
  useEffect(() => {
    const processId = propProcessId || paramProcessId || searchParams.get('processId');
    console.log('[CompanyProcessPage] Process ID from URL:', processId);

    if (processId) {
      // First try to find the process in already loaded data
      if (artifactsWithProcesses.length > 0) {
        for (const artifact of artifactsWithProcesses) {
          const process = artifact.processes.find((p) => p.id === processId);
          if (process) {
            console.log('[CompanyProcessPage] Found process in loaded data:', process);
            setSelectedProcess(process);
            setExpandedArtifacts((prev) => new Set(prev).add(artifact.id));
            return;
          }
        }
      }

      // If not found in loaded data, load it directly
      console.log('[CompanyProcessPage] Process not found in loaded data, loading directly...');
      void loadProcessById(processId);
    }
  }, [propProcessId, paramProcessId, searchParams, artifactsWithProcesses, loadProcessById]);

  // Load transitions when a process is selected
  useEffect(() => {
    if (selectedProcess) {
      void loadProcessTransitions(selectedProcess.id);
    } else {
      setProcessTransitions([]);
    }
  }, [selectedProcess, loadProcessTransitions]);

  // Load queue items (items in queue state)
  const loadQueueItems = useCallback(async () => {
    if (!selectedTree || !selectedCompany) {
      return;
    }

    setQueueLoading(true);
    setQueueError(null);

    try {
      // First, we need to find states with name "queue" or similar
      // For now, we'll fetch all items and filter by state name
      // In a real implementation, you'd query states first to find queue state IDs
      const allItems = await ArtifactItemService.getItemsByCompany(selectedCompany.id, {
        artifact_id: selectedProcess?.artifact_ref,
      });

      // Filter items that are in a "queue" state
      // This is a simplified approach - in production, you'd query by state name or use a state ID
      const queueStateItems = allItems.filter((item) => {
        // Check if item's current state name contains "queue" (case-insensitive)
        // This is a workaround - ideally we'd have state names in the item data
        return item.state?.toLowerCase().includes('queue') || 
               item.current_state_id || // If we have state_id, we'll need to check state name separately
               false;
      });

      setQueueItems(queueStateItems);
    } catch (error) {
      console.error('[CompanyProcessPage] Error loading queue items:', error);
      setQueueError('Failed to load queue items');
    } finally {
      setQueueLoading(false);
    }
  }, [selectedTree, selectedCompany, selectedProcess]);

  // Load available transitions for an item
  const loadAvailableTransitions = useCallback(async (item: ArtifactItem) => {
    if (!item.artifact_id || !item.id) {
      message.warning('Item missing required information');
      return;
    }

    setTransitionsLoadingForItem(true);
    try {
      const transitions = await StateMachineService.getAvailableTransitions(
        item.artifact_id,
        item.id
      );
      setAvailableTransitions(transitions);
    } catch (error: any) {
      console.error('[CompanyProcessPage] Error loading available transitions:', error);
      message.error('Failed to load available transitions: ' + (error.message || 'Unknown error'));
      setAvailableTransitions([]);
    } finally {
      setTransitionsLoadingForItem(false);
    }
  }, []);

  // Execute a transition on an item
  const handleExecuteTransition = useCallback(async (item: ArtifactItem, transitionId: string) => {
    if (!item.artifact_id || !item.id) {
      message.warning('Item missing required information');
      return;
    }

    setExecutingTransition(true);
    try {
      const transitionData = await transitionForm.validateFields();
      
      await StateMachineService.executeTransition(
        item.artifact_id,
        item.id,
        transitionId,
        {
          transition_data: transitionData.transition_data || {},
          reason: transitionData.reason || '',
          transitioned_by: undefined, // Will be set by backend from auth token
        }
      );

      message.success('Transition executed successfully');
      setShowTransitionModal(false);
      setSelectedItemForTransition(null);
      transitionForm.resetFields();
      
      // Reload queue items to reflect the state change
      await loadQueueItems();
    } catch (error: any) {
      console.error('[CompanyProcessPage] Error executing transition:', error);
      message.error('Failed to execute transition: ' + (error.response?.data?.detail || error.message || 'Unknown error'));
    } finally {
      setExecutingTransition(false);
    }
  }, [transitionForm, loadQueueItems]);

  // Open transition modal for an item
  const handleOpenTransitionModal = useCallback(async (item: ArtifactItem) => {
    setSelectedItemForTransition(item);
    setShowTransitionModal(true);
    await loadAvailableTransitions(item);
  }, [loadAvailableTransitions]);

  // Load queue items when view changes or process is selected
  useEffect(() => {
    if (activeView === 'queue') {
      void loadQueueItems();
    }
  }, [activeView, loadQueueItems, selectedProcess]);

  // Handle artifact expansion
  const handleArtifactToggle = (artifactId: string) => {
    setExpandedArtifacts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(artifactId)) {
        newSet.delete(artifactId);
      } else {
        newSet.add(artifactId);
      }
      return newSet;
    });
  };

  // Handle process selection
  const handleProcessSelect = (process: Process) => {
    setSelectedProcess(process);
    navigate(`/company-processes/${process.id}`);
  };

  // Handle process creation
  const handleCreateProcess = (artifactId: string) => {
    navigate(`/artifacts/${artifactId}/processes/create`);
  };

  const renderPageHeader = () => {
    if (selectedProcess) {
      return (
        <div className="page-header">
          <div className="header-content">
            <h1>Process Details</h1>
            <p>
              Viewing process: <strong>{selectedProcess.name}</strong>
            </p>
            <div className="process-context">
              <span className="process-type">{selectedProcess.process_type}</span>
              <span className="demand-flow">{selectedProcess.demand_flow_type}</span>
            </div>
          </div>
          <div className="header-actions">
            <button onClick={() => setSelectedProcess(null)} className="back-to-processes-btn">
              ← Back to All Processes
            </button>
          </div>
        </div>
      );
    } else {
      return (
        <div className="page-header">
          <div className="header-content">
            <h1>Company Processes</h1>
            <p>Manage all company processes by artifact</p>
          </div>
          <div className="header-actions">
            <Tabs
              activeKey={activeView}
              onChange={(key) => setActiveView(key as 'processes' | 'queue')}
              style={{ marginBottom: 0 }}
            >
              <Tabs.TabPane tab="Processes" key="processes" />
              <Tabs.TabPane tab={`Queue (${queueItems.length})`} key="queue" />
            </Tabs>
            <button onClick={() => navigate('/artifacts')} className="view-artifacts-btn">
              View Artifacts
            </button>
          </div>
        </div>
      );
    }
  };

  const renderContextInfo = () => {
    if (!selectedTree) {
      return (
        <div className="context-warning">
          <p>⚠️ No artifact tree selected. Please select an artifact tree to manage processes.</p>
          <button onClick={() => navigate(COMPANY_ARTIFACTS_SELECT_PATH)} className="select-tree-btn">
            Select Artifact Tree
          </button>
        </div>
      );
    }

    return (
      <div className="context-info">
        <div className="tree-info">
          <span className="tree-label">Artifact Tree:</span>
          <span className="tree-name">{selectedTree.name}</span>
        </div>
        <div className="stats-info">
          <span className="artifacts-count">{artifactsWithProcesses.length} artifacts</span>
          <span className="processes-count">
            {artifactsWithProcesses.reduce((sum, artifact) => sum + artifact.processes.length, 0)}{' '}
            processes
          </span>
        </div>
      </div>
    );
  };

  const renderArtifactTree = () => {
    if (artifactsLoading || processesLoading) {
      return (
        <div className="loading-container">
          <div className="loading-spinner">Loading artifacts and processes...</div>
        </div>
      );
    }

    if (artifactsError || processesError) {
      return (
        <div className="error-container">
          <div className="error-message">
            <h3>Error Loading Data</h3>
            <p>{artifactsError || processesError}</p>
            <button onClick={() => window.location.reload()}>Retry</button>
          </div>
        </div>
      );
    }

    if (artifactsWithProcesses.length === 0) {
      return (
        <div className="empty-state">
          <p>No artifacts found in this tree.</p>
          <button onClick={() => navigate('/artifacts')}>Go to Artifacts</button>
        </div>
      );
    }

    return (
      <div className="artifact-tree">
        {artifactsWithProcesses.map((artifact) => (
          <div key={artifact.id} className="artifact-node">
            <div
              className={`artifact-header ${expandedArtifacts.has(artifact.id) ? 'expanded' : ''}`}
              onClick={() => handleArtifactToggle(artifact.id)}
            >
              <div className="artifact-info">
                <span className="expand-icon">
                  {expandedArtifacts.has(artifact.id) ? '▼' : '▶'}
                </span>
                <span className="artifact-name">{artifact.name}</span>
                <span className="process-count">({artifact.processes.length} processes)</span>
              </div>
              <div className="artifact-actions">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCreateProcess(artifact.id);
                  }}
                  className="create-process-btn"
                  title="Create new process"
                >
                  + Add Process
                </button>
              </div>
            </div>

            {expandedArtifacts.has(artifact.id) && (
              <div className="processes-list">
                {artifact.processes.length === 0 ? (
                  <div className="no-processes">
                    <p>No processes for this artifact</p>
                    <button
                      onClick={() => handleCreateProcess(artifact.id)}
                      className="create-first-process-btn"
                    >
                      Create First Process
                    </button>
                  </div>
                ) : (
                  artifact.processes.map((process) => (
                    <div
                      key={process.id}
                      className={`process-item ${selectedProcess?.id === process.id ? 'selected' : ''}`}
                      onClick={() => handleProcessSelect(process)}
                    >
                      <div className="process-info">
                        <span className="process-name">{process.name}</span>
                        <span className="process-type">{process.process_type}</span>
                        <span className="demand-flow">{process.demand_flow_type}</span>
                      </div>
                      <div className="process-actions">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleProcessSelect(process);
                          }}
                          className="view-process-btn"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Group transitions by role
  const groupTransitionsByRole = (transitions: ProcessTransition[]) => {
    const groups: Record<string, ProcessTransition[]> = {};

    transitions.forEach((transition) => {
      const role = transition.transition_type || 'UNKNOWN';
      if (!groups[role]) {
        groups[role] = [];
      }
      groups[role].push(transition);
    });

    return groups;
  };

  // Render queue view showing items in queue state
  const renderQueueView = () => {
    if (queueLoading) {
      return (
        <div className="loading-container">
          <div className="loading-spinner">Loading queue items...</div>
        </div>
      );
    }

    if (queueError) {
      return (
        <div className="error-container">
          <div className="error-message">
            <h3>Error Loading Queue</h3>
            <p>{queueError}</p>
            <Button onClick={() => loadQueueItems()} icon={<ReloadOutlined />}>
              Retry
            </Button>
          </div>
        </div>
      );
    }

    if (queueItems.length === 0) {
      return (
        <div className="empty-state">
          <p>No items in queue state.</p>
          <Button onClick={() => loadQueueItems()} icon={<ReloadOutlined />}>
            Refresh
          </Button>
        </div>
      );
    }

    // Group items by process (if process information is available)
    const itemsByProcess = queueItems.reduce((acc, item) => {
      const processId = (item as any).process_id || 'unassigned';
      if (!acc[processId]) {
        acc[processId] = [];
      }
      acc[processId].push(item);
      return acc;
    }, {} as Record<string, ArtifactItem[]>);

    const columns = [
      {
        title: 'Item Name',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: 'Artifact',
        key: 'artifact',
        render: (_: any, item: ArtifactItem) => item.artifact?.name || item.artifact_id,
      },
      {
        title: 'State',
        dataIndex: 'state',
        key: 'state',
        render: (state: string) => <Tag color="orange">{state || 'Unknown'}</Tag>,
      },
      {
        title: 'Properties',
        key: 'properties',
        render: (_: any, item: ArtifactItem) => {
          const props = item.item_data || {};
          const propKeys = Object.keys(props).slice(0, 3);
          return (
            <Space size={4} wrap>
              {propKeys.map((key) => (
                <Tag key={key}>
                  {key}: {String(props[key])}
                </Tag>
              ))}
              {Object.keys(props).length > 3 && (
                <Tag>+{Object.keys(props).length - 3} more</Tag>
              )}
            </Space>
          );
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_: any, item: ArtifactItem) => (
          <Button
            type="primary"
            size="small"
            icon={<PlayCircleOutlined />}
            onClick={() => handleOpenTransitionModal(item)}
          >
            Execute Transition
          </Button>
        ),
      },
    ];

    return (
      <div className="queue-view">
        <Card
          title="Queue Items"
          extra={
            <Button icon={<ReloadOutlined />} onClick={() => loadQueueItems()}>
              Refresh
            </Button>
          }
        >
          {Object.keys(itemsByProcess).length > 1 ? (
            // Grouped by process
            Object.entries(itemsByProcess).map(([processId, items]) => (
              <Card
                key={processId}
                title={processId === 'unassigned' ? 'Unassigned Items' : `Process: ${processId}`}
                style={{ marginBottom: 16 }}
                size="small"
              >
                <Table
                  dataSource={items}
                  columns={columns}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                  size="small"
                />
              </Card>
            ))
          ) : (
            // Single table if all items are in one process or unassigned
            <Table
              dataSource={queueItems}
              columns={columns}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          )}
        </Card>
      </div>
    );
  };

  const renderProcessDetails = () => {
    if (!selectedProcess) return null;

    const transitionGroups = groupTransitionsByRole(processTransitions);

    return (
      <div className="process-details">
        <div className="process-details-header">
          <h2>{selectedProcess.name}</h2>
          <p>{selectedProcess.description || 'No description available'}</p>
          <div className="process-actions">
            <button
              className="add-transition-btn"
              onClick={() => {
                // TODO: Implement add transition
                console.log('Add transition clicked');
              }}
            >
              + Add Transition
            </button>
          </div>
        </div>

        <div className="process-transitions">
          {transitionsLoading ? (
            <div className="loading-container">
              <div className="loading-spinner">Loading transitions...</div>
            </div>
          ) : transitionsError ? (
            <div className="error-container">
              <div className="error-message">
                <h3>Error Loading Transitions</h3>
                <p>{transitionsError}</p>
                <button
                  onClick={() => selectedProcess && loadProcessTransitions(selectedProcess.id)}
                >
                  Retry
                </button>
              </div>
            </div>
          ) : Object.keys(transitionGroups).length === 0 ? (
            <div className="empty-transitions">
              <p>No transitions found for this process.</p>
              <button
                className="add-first-transition-btn"
                onClick={() => {
                  // TODO: Implement add transition
                  console.log('Add first transition clicked');
                }}
              >
                Add First Transition
              </button>
            </div>
          ) : (
            <div className="transition-groups">
              {Object.entries(transitionGroups).map(([role, transitions]) => (
                <div key={role} className="transition-group">
                  <div className="group-header">
                    <h3>{role}</h3>
                    <span className="transition-count">({transitions.length} transitions)</span>
                  </div>
                  <div className="transitions-list">
                    {transitions.map((transition) => (
                      <div key={transition.id} className="transition-item">
                        <div className="transition-info">
                          <div className="artifact-info">
                            <span className="artifact-name">
                              {transition.artifact_name || transition.artifact_ref}
                            </span>
                          </div>
                          <div className="state-flow">
                            <span className="input-state">
                              {transition.input_state_name || transition.input_state_ref}
                            </span>
                            <span className="arrow">→</span>
                            <span className="output-state">
                              {transition.output_state_name || transition.output_state_ref}
                            </span>
                          </div>
                          <div className="transition-meta">
                            <span className="transition-name">{transition.name}</span>
                            {transition.description && (
                              <span className="transition-description">
                                {transition.description}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="transition-actions">
                          <button
                            className="edit-transition-btn"
                            onClick={() => {
                              // TODO: Implement edit transition
                              console.log('Edit transition clicked:', transition.id);
                            }}
                            title="Edit transition"
                          >
                            ✏️
                          </button>
                          <button
                            className="delete-transition-btn"
                            onClick={() => {
                              // TODO: Implement delete transition
                              console.log('Delete transition clicked:', transition.id);
                            }}
                            title="Delete transition"
                          >
                            🗑️
                          </button>
                          <button
                            className="navigate-transition-btn"
                            onClick={() => {
                              // TODO: Navigate to related processes
                              console.log('Navigate to related processes:', transition.id);
                            }}
                            title="View related processes"
                          >
                            🔗
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!selectedTree) {
    return (
      <div className="company-process-page">
        <div className="no-tree-selected">
          <h2>No Artifact Tree Selected</h2>
          <p>Please select an artifact tree to view processes.</p>
          <button onClick={() => navigate(COMPANY_ARTIFACTS_SELECT_PATH)} className="select-tree-btn">
            Select Artifact Tree
          </button>
        </div>
      </div>
    );
  }

  // Check if we should show process details based on URL
  const processIdFromUrl = propProcessId || paramProcessId || searchParams.get('processId');
  const shouldShowProcessDetails = processIdFromUrl && selectedProcess;

  return (
    <div className="company-process-page">
      {renderPageHeader()}
      {renderContextInfo()}

      <div className="process-management-container">
        {shouldShowProcessDetails ? (
          renderProcessDetails()
        ) : activeView === 'queue' ? (
          renderQueueView()
        ) : (
          renderArtifactTree()
        )}
      </div>

      {/* Transition Execution Modal */}
      <Modal
        title="Execute Transition"
        open={showTransitionModal}
        onCancel={() => {
          setShowTransitionModal(false);
          setSelectedItemForTransition(null);
          setAvailableTransitions([]);
          transitionForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        {selectedItemForTransition && (
          <div>
            <p>
              <strong>Item:</strong> {selectedItemForTransition.name}
            </p>
            <p>
              <strong>Current State:</strong> {selectedItemForTransition.state || 'Unknown'}
            </p>

            {transitionsLoadingForItem ? (
              <div>Loading available transitions...</div>
            ) : availableTransitions.length === 0 ? (
              <div style={{ color: '#999', marginTop: 16 }}>
                No available transitions for this item in its current state.
              </div>
            ) : (
              <Form form={transitionForm} layout="vertical" style={{ marginTop: 16 }}>
                <Form.Item
                  label="Select Transition"
                  name="transition_id"
                  rules={[{ required: true, message: 'Please select a transition' }]}
                >
                  <select style={{ width: '100%', padding: '8px' }}>
                    <option value="">Select a transition...</option>
                    {availableTransitions.map((transition) => (
                      <option key={transition.id} value={transition.id}>
                        {transition.name} → {transition.to_state_name || transition.output_state_ref}
                        {transition.can_execute ? '' : ' (Cannot execute)'}
                      </option>
                    ))}
                  </select>
                </Form.Item>

                <Form.Item label="Reason (optional)" name="reason">
                  <Input.TextArea rows={3} placeholder="Reason for this transition" />
                </Form.Item>

                <Form.Item label="Transition Data (optional)" name="transition_data">
                  <Input.TextArea
                    rows={4}
                    placeholder='JSON data, e.g., {"quantity": 1, "notes": "Starting production"}'
                  />
                </Form.Item>

                <Form.Item>
                  <Space>
                    <Button
                      type="primary"
                      icon={<PlayCircleOutlined />}
                      onClick={() => {
                        const transitionId = transitionForm.getFieldValue('transition_id');
                        if (transitionId && selectedItemForTransition) {
                          void handleExecuteTransition(selectedItemForTransition, transitionId);
                        }
                      }}
                      loading={executingTransition}
                      disabled={!transitionForm.getFieldValue('transition_id')}
                    >
                      Execute Transition
                    </Button>
                    <Button
                      onClick={() => {
                        setShowTransitionModal(false);
                        setSelectedItemForTransition(null);
                        transitionForm.resetFields();
                      }}
                    >
                      Cancel
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CompanyProcessPage;
